import { useTheme } from '@emotion/react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { ReactComponent as CloseIcon } from 'src/assets/svg/close-icon.svg';
import { Section } from 'src/components/section';
import { ControlledField } from 'src/components/controlled-field';
import { Button } from 'src/components/button';
import { useResetPasswordSupabase } from 'src/auth/hooks/use-reset-password-supabase';

import { forgottenPasswordDataKeys } from '../constants';
import { forgottenPasswordDataSchema } from '../login-schema';

import { styles } from './styles';

import type { ForgottenPasswordFormProps } from './types';
import type { ForgottenPasswordDataFormValues } from '../types';

export const ForgottenPasswordForm: FC<ForgottenPasswordFormProps> = ({
    onClose,
    hidePasswordReset,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { control, handleSubmit, watch } = useForm<ForgottenPasswordDataFormValues>({
        resolver: yupResolver(
            forgottenPasswordDataSchema
        ) as Resolver<ForgottenPasswordDataFormValues>,
        defaultValues: { email: '' },
    });

    const watchEmailField = watch('email');

    const { mutate: resetPassword, isSuccess, isPending, error } = useResetPasswordSupabase();

    const handleClose = () => {
        onClose();
    };

    const onResetPassword = handleSubmit((data) => {
        resetPassword({ email: data.email });
    });

    const handleSubmitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!isSuccess && watchEmailField) onResetPassword();
        }
    };

    return (
        <Box css={styles.forgottenPasswordWrapper}>
            <Box>
                <Box css={styles.back} onClick={hidePasswordReset}>
                    <BackArrow css={styles.backArrowIcon} />
                    <Typography>{t('agreements_back_button_content')}</Typography>
                </Box>
            </Box>
            <Section
                header={
                    !isSuccess
                        ? t('login_dialog_forgotten_password')
                        : t('forgotten_password_email_sent_title')
                }
            >
                {error && (
                    <Box css={styles.loginDialogErrorBox}>
                        <Typography color={theme.palette.primary.main} variant="subtitle1">
                            {error.message}
                        </Typography>
                    </Box>
                )}

                <Typography css={styles.forgottenPasswordSubtitle} variant="subtitle1">
                    {!isSuccess
                        ? t('forgotten_password_text')
                        : t('forgotten_password_email_sent_text')}
                </Typography>

                <Stack
                    spacing="20px"
                    css={styles.forgottenPasswordFormWrapper}
                    onKeyDown={handleSubmitOnEnter}
                >
                    <ControlledField
                        type="email"
                        name={forgottenPasswordDataKeys.email}
                        control={control}
                        label={t('login_dialog_email_address')}
                        placeholder={t('login_dialog_email_address')}
                        disabled={isSuccess}
                    />
                </Stack>

                {!isSuccess && (
                    <Button
                        disableElevation
                        css={styles.forgottenPasswordButton}
                        variant="contained"
                        loading={isPending}
                        onClick={onResetPassword}
                        disabled={!watchEmailField}
                    >
                        {t('forgotten_password_cta')}
                    </Button>
                )}
            </Section>

            <IconButton css={styles.forgottenPasswordCloseButton} onClick={handleClose}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
};
